import React, { useMemo, useState } from "react";
import { Grid, MenuItem, Stack, TextField } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import FormulaDetails from "./FormulaDetails";
import { usePlatform } from "../platform/store";
import {
  InlineActions,
  PageRoot,
  PrimaryAction,
  RecordCard,
  SectionCard,
} from "./ui/PlatformPrimitives";

const PerfumeDashboard = () => {
  const { products } = usePlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramProductId = searchParams.get("product");
  const [selectedProductId, setSelectedProductId] = useState(
    paramProductId || products[0]?.id || ""
  );

  const selectProduct = (id) => {
    setSelectedProductId(id);
    setSearchParams({ product: id }, { replace: true });
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [marketFilter, setMarketFilter] = useState("");

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesSearch =
          !searchQuery ||
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.brand.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesMarket = !marketFilter || product.markets.includes(marketFilter);
        return matchesSearch && matchesMarket;
      }),
    [marketFilter, products, searchQuery]
  );

  const selectedProduct =
    products.find((product) => product.id === selectedProductId) || filteredProducts[0] || null;

  return (
    <PageRoot>
      <SectionCard
        title="Formula Library"
        action={
          <InlineActions>
            <PrimaryAction component={RouterLink} to="/compliance-checker">
              New formula
            </PrimaryAction>
          </InlineActions>
        }
      >
        <InlineActions>
          <TextField
            size="small"
            placeholder="Search…"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            select
            value={marketFilter}
            onChange={(event) => setMarketFilter(event.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All markets</MenuItem>
            <MenuItem value="EU">EU</MenuItem>
            <MenuItem value="US">US</MenuItem>
          </TextField>
        </InlineActions>
      </SectionCard>

      <Grid container spacing={2}>
        <Grid
          size={{ xs: 12, md: 4 }}
          sx={{
            // Sticky on Grid item; alignSelf:flex-start opts out of flex
            // stretch so the item is its own (short) height while the row is
            // tall — that's the gap sticky needs to pin within.
            position: { xs: "static", md: "sticky" },
            top: { md: 16 },
            alignSelf: "flex-start",
          }}
        >
          <Stack
            spacing={1.5}
            sx={{
              // Stack instead of nested Grid v2 container — Grid v2's
              // negative-margin spacing inside an overflow scroll region
              // causes horizontal scrollbars to leak through to the page.
              // Stack uses `gap` (no negative margins), so layout stays
              // self-contained.
              //
              // No solid bg here — the page gradient shows through so the
              // child RecordCards (auto-glass via MuiCard) refract the
              // background instead of stacking on a flat surface.
              maxHeight: { md: "calc(100vh - 96px)" },
              overflowY: { md: "auto" },
              overflowX: "hidden",
              // Top/bottom padding so card corners don't touch the scroll edge
              // (the scrollport itself would otherwise mask the rounded tops).
              pr: { md: 0.5 },
              py: 0.5,
              minWidth: 0,
              // Transparent scroll region — no borderRadius here, otherwise the
              // overflow clip flattens the rounded tops of the cards inside.
              background: "transparent",
            }}
          >
            {filteredProducts.map((product) => (
              <RecordCard
                key={product.id}
                title={product.name}
                subtitle={product.brand}
                status={product.compliance.overallStatus}
                chips={product.markets}
                onClick={() => selectProduct(product.id)}
                selected={selectedProduct?.id === product.id}
              />
            ))}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }} sx={{ minWidth: 0 }}>
          <FormulaDetails product={selectedProduct} />
        </Grid>
      </Grid>
    </PageRoot>
  );
};

export default PerfumeDashboard;
